'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  ChevronRight,
  CircleAlert,
  Clock3,
  Copy,
  FileCheck2,
  HardHat,
  MapPin,
  Plus,
  Search,
  Settings,
  UserPlus,
  Users,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  EmptyState,
  ErrorState,
  LoadingState,
  PageTitle,
} from '@/components/ui/common';
import { Modal } from '@/components/ui/modal';
import { useToast } from '@/components/ui/toast';
import { useTeamPermissions } from '@/hooks/use-team-permissions';
import { api } from '@/lib/api/client';
import {
  listItems,
  queryKeys,
  replaceCurrentTeamCache,
} from '@/lib/api/query';
import { formatDate, initials } from '@/lib/utils';
import type { TeamInvitationResult, TeamRole } from '@/types/platform';

export function TeamsPage() {
  const router = useRouter();
  const client = useQueryClient();
  const teams = useQuery({
    queryKey: queryKeys.teams,
    queryFn: api.teams.list,
  });
  const [search, setSearch] = useState('');
  const items = listItems(teams.data).filter((team) =>
    team.name.toLowerCase().includes(search.toLowerCase()),
  );
  async function enterTeam(teamId: string) {
    const team = await api.teams.select(teamId);
    replaceCurrentTeamCache(client, team);
    router.push('/team');
  }
  return (
    <>
      <PageTitle
        eyebrow="Workspace"
        title="내 팀"
        description="참여 중인 팀과 각 현장 운영 공간을 선택하세요."
        actions={
          <Link className="button-primary" href="/teams/new">
            <Plus className="size-4" />새 팀
          </Link>
        }
      />
      <div className="mx-auto max-w-7xl p-5 lg:p-8">
        <div className="mb-5 flex items-center justify-between">
          <label className="relative block w-full max-w-sm">
            <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
            <input
              className="field pl-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="팀 이름 검색"
            />
          </label>
          <span className="hidden text-sm text-muted-foreground sm:block">
            {items.length}개 팀
          </span>
        </div>
        {teams.isPending ? (
          <LoadingState />
        ) : teams.isError ? (
          <div className="surface">
            <ErrorState error={teams.error} retry={() => teams.refetch()} />
          </div>
        ) : !items.length ? (
          <div className="surface">
            <EmptyState
              icon={Users}
              title="아직 참여 중인 팀이 없습니다"
              description="첫 작업 공간을 만들거나 받은 팀 초대를 확인하세요."
              action={
                <Link href="/teams/new" className="button-primary">
                  <Plus className="size-4" />첫 팀 만들기
                </Link>
              }
            />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((team) => (
              <button
                key={team.id}
                type="button"
                onClick={() => enterTeam(team.id)}
                className="group surface overflow-hidden transition-transform hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-panel"
              >
                <div className="flex items-start gap-4 p-5">
                  <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary-100 to-blue-50 font-black text-primary-700 dark:from-primary-900 dark:to-blue-950 dark:text-blue-200">
                    {initials(team.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <strong className="truncate text-base">
                        {team.name}
                      </strong>
                      <Badge tone="blue">{team.myRole}</Badge>
                    </span>
                    <span className="mt-1 line-clamp-2 block min-h-10 text-sm leading-5 text-muted-foreground">
                      {team.description || '팀 설명이 아직 없습니다.'}
                    </span>
                  </span>
                  <ChevronRight className="size-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
                </div>
                <div className="grid grid-cols-2 border-t bg-muted/30 px-5 py-3 text-xs text-muted-foreground">
                  <span>
                    <strong className="mr-1 text-foreground">
                      {team.siteCount ?? 0}
                    </strong>
                    현장
                  </span>
                  <span>
                    <strong className="mr-1 text-foreground">
                      {team.memberCount ?? 1}
                    </strong>
                    팀원
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export function NewTeamPage() {
  const router = useRouter();
  const client = useQueryClient();
  const [error, setError] = useState('');
  const create = useMutation({
    mutationFn: api.teams.create,
    onSuccess: async (team) => {
      const selectedTeam = await api.teams.select(team.id);
      replaceCurrentTeamCache(client, selectedTeam);
      router.replace('/team/sites');
    },
    onError: (reason) => setError(reason.message),
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    create.mutate({
      name: String(data.get('name')),
      description: String(data.get('description') || ''),
    });
  }
  return (
    <>
      <PageTitle
        eyebrow="New workspace"
        title="새 팀 만들기"
        description="여러 현장을 함께 운영할 팀 작업 공간을 생성합니다."
      />
      <div className="mx-auto max-w-2xl p-5 lg:p-8">
        <form onSubmit={submit} className="surface p-6 sm:p-8">
          <div className="mb-7 flex gap-4">
            <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-blue-50 text-primary-600 dark:bg-blue-950">
              <Users className="size-6" />
            </span>
            <div>
              <h2 className="text-base font-bold">팀 기본 정보</h2>
              <p className="mt-1 text-sm leading-5 text-muted-foreground">
                생성한 계정이 OWNER가 되며 이후 팀원을 초대할 수 있습니다.
              </p>
            </div>
          </div>
          <div className="space-y-5">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold">
                팀 이름 <b className="text-red-500">*</b>
              </span>
              <input
                required
                maxLength={80}
                name="name"
                className="field"
                placeholder="예: SEEXR 시공사업부"
              />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold">설명</span>
              <textarea
                name="description"
                rows={4}
                className="field h-auto py-3"
                placeholder="팀의 역할과 담당 프로젝트를 간단히 적어 주세요."
              />
            </label>
            {error && (
              <p className="rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Link href="/teams" className="button-secondary">
                취소
              </Link>
              <button disabled={create.isPending} className="button-primary">
                팀 만들기 <ArrowRight className="size-4" />
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export function TeamDashboardPage() {
  const permissions = useTeamPermissions();
  const dashboard = useQuery({
    queryKey: queryKeys.teamDashboard,
    queryFn: api.teams.dashboard,
  });
  const team = useQuery({
    queryKey: queryKeys.team,
    queryFn: api.teams.get,
  });
  const data = dashboard.data;
  const stats = [
    {
      label: '운영 현장',
      value: data?.totalSites ?? 0,
      icon: Building2,
      tone: 'text-blue-600 bg-blue-50 dark:bg-blue-950',
    },
    {
      label: '오늘 출역',
      value: data?.todayAttendance ?? 0,
      suffix: '명',
      icon: HardHat,
      tone: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950',
    },
    {
      label: '미결 승인',
      value: data?.pendingApprovals ?? 0,
      suffix: '건',
      icon: FileCheck2,
      tone: 'text-amber-600 bg-amber-50 dark:bg-amber-950',
    },
    {
      label: '팀원',
      value: data?.totalMembers ?? 0,
      suffix: '명',
      icon: Users,
      tone: 'text-violet-600 bg-violet-50 dark:bg-violet-950',
    },
  ];
  return (
    <>
      <PageTitle
        eyebrow="Team overview"
        title={team.data?.name ?? '팀 대시보드'}
        description="모든 현장의 진행 상황과 오늘의 운영 지표를 확인하세요."
        actions={
          permissions.canManage && (
            <Link href="/team/sites" className="button-primary">
              <Plus className="size-4" />
              현장 추가
            </Link>
          )
        }
      />
      <div className="mx-auto max-w-[1500px] space-y-6 p-5 lg:p-8">
        {dashboard.isPending ? (
          <LoadingState />
        ) : dashboard.isError ? (
          <div className="surface">
            <ErrorState
              error={dashboard.error}
              retry={() => dashboard.refetch()}
            />
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {stats.map(({ label, value, suffix, icon: Icon, tone }) => (
                <div
                  key={label}
                  className="surface flex items-center gap-4 p-5"
                >
                  <span
                    className={`grid size-11 place-items-center rounded-xl ${tone}`}
                  >
                    <Icon className="size-5" />
                  </span>
                  <div>
                    <p className="text-sm text-muted-foreground">{label}</p>
                    <p className="mt-1 text-2xl font-black tracking-tight">
                      {value}
                      <small className="ml-1 text-sm font-semibold text-muted-foreground">
                        {suffix}
                      </small>
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <section className="surface overflow-hidden">
              <div className="flex items-center justify-between border-b px-5 py-4">
                <div>
                  <h2 className="font-bold">현장 포트폴리오</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    계획 대비 진척과 오늘의 투입 현황
                  </p>
                </div>
                <Link
                  href="/team/sites"
                  className="text-sm font-bold text-primary-600"
                >
                  전체 보기
                </Link>
              </div>
              {!data?.sites.length ? (
                <EmptyState
                  icon={Building2}
                  title="첫 현장을 등록해 주세요"
                  description="공정과 출역 데이터를 관리할 프로젝트 현장을 추가하세요."
                  action={
                    permissions.canManage && (
                      <Link className="button-primary" href="/team/sites">
                        <Plus className="size-4" />
                        현장 등록
                      </Link>
                    )
                  }
                />
              ) : (
                <div className="grid divide-y xl:grid-cols-2 xl:divide-x xl:divide-y-0">
                  {data.sites.map((site) => (
                    <Link
                      href={`/team/sites/${site.id}`}
                      key={site.id}
                      className="group p-5 hover:bg-muted/30"
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <Badge tone="green">운영 중</Badge>
                          <h3 className="mt-3 truncate text-base font-bold group-hover:text-primary-600">
                            {site.name}
                          </h3>
                          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin className="size-3" />
                            {site.address || '주소 미등록'}
                          </p>
                        </div>
                        <span className="text-2xl font-black text-primary-600">
                          {site.progress ?? 0}
                          <small className="text-sm">%</small>
                        </span>
                      </div>
                      <div className="mt-5 h-2 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary-500 to-blue-400"
                          style={{ width: `${site.progress ?? 0}%` }}
                        />
                      </div>
                      <div className="mt-4 flex gap-5 text-xs text-muted-foreground">
                        <span>
                          <HardHat className="mr-1 inline size-3.5" />
                          오늘 {site.todayAttendance ?? 0}명
                        </span>
                        <span>
                          <Clock3 className="mr-1 inline size-3.5" />
                          대기 {site.pendingReports ?? 0}건
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}

export function SitesPage() {
  const client = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const sites = useQuery({
    queryKey: queryKeys.sites,
    queryFn: api.teams.sites,
  });
  const items = listItems(sites.data).filter((site) =>
    site.name.toLowerCase().includes(search.toLowerCase()),
  );
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.teams.createSite(body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.sites });
      setOpen(false);
      toast('현장을 등록했습니다.');
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    create.mutate({
      name: String(data.get('name')),
      description: String(data.get('description') || ''),
      location: String(data.get('address') || ''),
      startDate: data.get('plannedStart') || undefined,
      endDate: data.get('plannedFinish') || undefined,
    });
  }
  return (
    <>
      <PageTitle
        eyebrow="Sites"
        title="현장 관리"
        description="팀이 운영하는 프로젝트 현장을 생성하고 전환합니다."
        actions={
          <button onClick={() => setOpen(true)} className="button-primary">
            <Plus className="size-4" />
            현장 등록
          </button>
        }
      />
      <div className="mx-auto max-w-7xl p-5 lg:p-8">
        <label className="relative mb-5 block max-w-sm">
          <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
          <input
            className="field pl-9"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="현장 검색"
          />
        </label>
        {sites.isPending ? (
          <LoadingState />
        ) : sites.isError ? (
          <div className="surface">
            <ErrorState error={sites.error} />
          </div>
        ) : !items.length ? (
          <div className="surface">
            <EmptyState
              icon={Building2}
              title="등록된 현장이 없습니다"
              description="첫 현장을 등록해 일정, 인력, 안전 데이터를 한곳에서 관리하세요."
              action={
                <button
                  className="button-primary"
                  onClick={() => setOpen(true)}
                >
                  <Plus className="size-4" />
                  현장 등록
                </button>
              }
            />
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {items.map((site) => (
              <Link
                className="surface group overflow-hidden hover:border-primary-200 hover:shadow-panel"
                key={site.id}
                href={`/team/sites/${site.id}`}
              >
                <div className="h-2 bg-gradient-to-r from-primary-600 via-blue-400 to-cyan-400" />
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <Badge
                      tone={site.status === 'ARCHIVED' ? 'neutral' : 'green'}
                    >
                      {site.status === 'ARCHIVED' ? '보관' : '운영 중'}
                    </Badge>
                    <ChevronRight className="size-5 text-muted-foreground transition-transform group-hover:translate-x-1" />
                  </div>
                  <h2 className="mt-4 text-lg font-bold group-hover:text-primary-600">
                    {site.name}
                  </h2>
                  <p className="mt-2 flex min-h-10 items-start gap-1.5 text-sm leading-5 text-muted-foreground">
                    <MapPin className="mt-0.5 size-4 shrink-0" />
                    {site.address || site.description || '현장 정보 미등록'}
                  </p>
                  <div className="mt-5 flex items-center justify-between border-t pt-4 text-xs text-muted-foreground">
                    <span>
                      <CalendarDays className="mr-1 inline size-3.5" />
                      {formatDate(site.plannedStart)}
                    </span>
                    <strong className="text-primary-600">
                      {site.progress ?? 0}% 진행
                    </strong>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="새 현장 등록"
        description="현장 생성 후 공정과 투입 인력을 등록할 수 있습니다."
      >
        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold">현장명</span>
            <input
              required
              name="name"
              className="field"
              placeholder="예: 반포 업무시설 신축공사"
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold">주소</span>
            <input name="address" className="field" placeholder="도로명 주소" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold">
                착공 예정
              </span>
              <input name="plannedStart" type="date" className="field" />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold">
                준공 예정
              </span>
              <input name="plannedFinish" type="date" className="field" />
            </label>
          </div>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold">설명</span>
            <textarea
              name="description"
              rows={3}
              className="field h-auto py-3"
            />
          </label>
          {create.error && (
            <p className="text-sm text-red-600">{create.error.message}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="button-secondary"
            >
              취소
            </button>
            <button disabled={create.isPending} className="button-primary">
              등록하기
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

export function MembersPage() {
  const client = useQueryClient();
  const toast = useToast();
  const permissions = useTeamPermissions();
  const [open, setOpen] = useState(false);
  const [createdInvitation, setCreatedInvitation] =
    useState<TeamInvitationResult | null>(null);
  const [copied, setCopied] = useState(false);
  const members = useQuery({
    queryKey: queryKeys.members,
    queryFn: api.teams.members,
  });
  const items = listItems(members.data);
  const invite = useMutation({
    mutationFn: (body: { email: string; role: Exclude<TeamRole, 'OWNER'> }) =>
      api.teams.invite(body),
    onSuccess: (invitation) => {
      setCreatedInvitation(invitation);
      setCopied(false);
      toast('초대 링크를 생성했습니다.');
      client.invalidateQueries({ queryKey: queryKeys.members });
    },
  });
  const invitationUrl = createdInvitation
    ? `${window.location.origin}/invitations/${encodeURIComponent(createdInvitation.token)}`
    : '';
  async function copyInvitationUrl() {
    await navigator.clipboard.writeText(invitationUrl);
    setCopied(true);
    toast('초대 링크를 복사했습니다.');
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    invite.mutate({
      email: String(data.get('email')),
      role: String(data.get('role')) as Exclude<TeamRole, 'OWNER'>,
    });
  }
  return (
    <>
      <PageTitle
        eyebrow="Members"
        title="팀원 관리"
        description="팀 역할은 모든 현장에 동일하게 적용됩니다."
        actions={
          permissions.canManage && (
            <button
              className="button-primary"
              onClick={() => {
                setCreatedInvitation(null);
                setOpen(true);
              }}
            >
              <UserPlus className="size-4" />
              팀원 초대
            </button>
          )
        }
      />
      <div className="mx-auto max-w-6xl p-5 lg:p-8">
        <div className="surface overflow-x-auto">
          {members.isPending ? (
            <LoadingState />
          ) : members.isError ? (
            <ErrorState error={members.error} />
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>팀원</th>
                  <th>역할</th>
                  <th>참여일</th>
                  <th>권한 범위</th>
                </tr>
              </thead>
              <tbody>
                {items.map((member) => (
                  <tr key={member.userId}>
                    <td>
                      <div className="flex items-center gap-3">
                        <span className="grid size-9 place-items-center rounded-full bg-muted text-xs font-bold">
                          {initials(member.name || member.email)}
                        </span>
                        <span>
                          <strong className="block">
                            {member.name || '이름 미등록'}
                          </strong>
                          <small className="text-muted-foreground">
                            {member.email}
                          </small>
                        </span>
                      </div>
                    </td>
                    <td>
                      <Badge
                        tone={member.role === 'OWNER' ? 'blue' : 'neutral'}
                      >
                        {member.role}
                      </Badge>
                    </td>
                    <td className="text-muted-foreground">
                      {formatDate(member.joinedAt)}
                    </td>
                    <td className="text-muted-foreground">
                      {member.role === 'VIEWER'
                        ? '조회 · 다운로드'
                        : member.role === 'EDITOR'
                          ? '운영 데이터 편집'
                          : '팀 · 현장 관리'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <Modal
        open={open && permissions.canManage}
        onClose={() => {
          setOpen(false);
          setCreatedInvitation(null);
        }}
        title="팀원 초대"
        description="초대 링크를 생성한 뒤 대상자에게 직접 전달해 주세요."
      >
        {createdInvitation ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950">
              <div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-200">
                <Check className="size-4" /> 초대 링크가 준비되었습니다
              </div>
              <p className="mt-2 text-xs leading-5 text-emerald-700 dark:text-emerald-300">
                이메일은 자동으로 발송되지 않습니다. 아래 링크를{' '}
                {createdInvitation.email} 사용자에게 직접 전달하세요.
              </p>
            </div>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold">
                초대 링크
              </span>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={invitationUrl}
                  className="field min-w-0 flex-1"
                  onFocus={(event) => event.currentTarget.select()}
                />
                <button
                  type="button"
                  className="button-secondary shrink-0"
                  onClick={copyInvitationUrl}
                >
                  {copied ? (
                    <Check className="size-4" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                  {copied ? '복사됨' : '복사'}
                </button>
              </div>
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="button-secondary"
                onClick={() => setCreatedInvitation(null)}
              >
                다른 초대 만들기
              </button>
              <button
                type="button"
                className="button-primary"
                onClick={() => {
                  setOpen(false);
                  setCreatedInvitation(null);
                }}
              >
                완료
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold">이메일</span>
              <input
                required
                name="email"
                type="email"
                className="field"
                placeholder="member@company.com"
              />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-semibold">역할</span>
              <select name="role" className="field">
                <option value="EDITOR">EDITOR · 운영 데이터 편집</option>
                <option value="ADMIN">ADMIN · 팀 및 현장 관리</option>
                <option value="VIEWER">VIEWER · 조회 전용</option>
              </select>
            </label>
            <p className="rounded-md bg-blue-50 p-3 text-xs leading-5 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
              OWNER 역할은 초대가 아니라 소유권 이전으로만 변경할 수 있습니다.
            </p>
            {invite.error && (
              <p className="text-sm text-red-600">{invite.error.message}</p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="button-secondary"
              >
                취소
              </button>
              <button className="button-primary" disabled={invite.isPending}>
                초대 링크 생성
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

export function TeamSettingsPage() {
  const team = useQuery({
    queryKey: queryKeys.team,
    queryFn: api.teams.get,
  });
  const client = useQueryClient();
  const toast = useToast();
  const update = useMutation({
    mutationFn: (body: { name?: string; description?: string }) =>
      api.teams.update(body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: queryKeys.team });
      toast('팀 설정을 저장했습니다.');
    },
  });
  const archive = useMutation({
    mutationFn: api.teams.archive,
    onSuccess: () => {
      toast('팀을 보관했습니다.');
      location.href = '/teams';
    },
  });
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    update.mutate({
      name: String(data.get('name')),
      description: String(data.get('description')),
    });
  }
  return (
    <>
      <PageTitle
        eyebrow="Settings"
        title="팀 설정"
        description="팀 정보와 작업 공간 상태를 관리합니다."
      />
      <div className="mx-auto max-w-3xl space-y-6 p-5 lg:p-8">
        <form onSubmit={submit} className="surface p-6">
          <div className="mb-6 flex items-center gap-3">
            <Settings className="size-5 text-primary-600" />
            <h2 className="font-bold">기본 정보</h2>
          </div>
          {team.isPending ? (
            <LoadingState />
          ) : (
            <div className="space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold">
                  팀 이름
                </span>
                <input
                  name="name"
                  defaultValue={team.data?.name}
                  className="field"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold">설명</span>
                <textarea
                  name="description"
                  defaultValue={team.data?.description ?? ''}
                  className="field h-auto py-3"
                  rows={4}
                />
              </label>
              <div className="flex justify-end">
                <button disabled={update.isPending} className="button-primary">
                  변경 저장
                </button>
              </div>
            </div>
          )}
        </form>
        <section className="surface border-red-200 p-6 dark:border-red-900">
          <div className="flex gap-3">
            <CircleAlert className="size-5 shrink-0 text-red-600" />
            <div className="flex-1">
              <h2 className="font-bold">팀 보관</h2>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                보관하면 모든 현장이 읽기 전용 상태가 됩니다. 데이터는 영구
                삭제되지 않습니다.
              </p>
              <button
                onClick={() => {
                  if (confirm('이 팀을 보관하시겠습니까?')) archive.mutate();
                }}
                className="mt-4 inline-flex h-9 items-center rounded-md border border-red-300 px-3 text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              >
                팀 보관하기
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
